import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { FONT_STACK, mint } from '@madar/shared';
import type { Point } from '../data';
import type { Run } from './facade';
import { MAT, waterNormals } from './look';

/**
 * EVERYTHING IN THIS FILE IS ILLUSTRATIVE.
 *
 * Ghassan asked to see how the building could look before a real model
 * exists ("fill the gaps yourself for now", 2026-10-21), so the details the
 * plan does not hold are invented here, each to a plausible size: stone
 * joints, paving, furniture, planting, trees, plant on the roof, signs, the
 * sea and the towers around. None of it is a claim about مطل; the page's
 * footnote says the surroundings and façades are illustrative. A modelled
 * glTF replaces the building; the surroundings can stay until a site model
 * exists.
 */

// ── Textures ─────────────────────────────────────────────────────────────────

/**
 * One panel with its joints, tiled at real size. ExtrudeGeometry's UVs are in
 * metres, so `repeat` is 1 / panel size.
 */
function jointTexture(fill: string, joint: string, panelW: number, panelH: number, grain = 0.05) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const g = canvas.getContext('2d')!;
  g.fillStyle = fill;
  g.fillRect(0, 0, 256, 128);
  // Grain and a little cloudiness, so a large wall is not one flat value.
  for (let i = 0; i < 1400; i++) {
    const dark = Math.random() < 0.5;
    g.fillStyle = dark
      ? `rgba(60,45,30,${Math.random() * grain})`
      : `rgba(255,250,240,${Math.random() * grain})`;
    const s = 1 + Math.random() * 3;
    g.fillRect(Math.random() * 256, Math.random() * 128, s, s);
  }
  g.strokeStyle = joint;
  g.lineWidth = 3;
  g.strokeRect(0, 0, 256, 128);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1 / panelW, 1 / panelH);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}

/** Limestone cladding on the cores, 1.2 × 0.6 m panels as on the built walls. */
export const stoneTexture = () => jointTexture(MAT.stone, MAT.stoneJoint, 1.2, 0.6, 0.07);

/** Plaza paving, 1 × 0.5 m. */
export const pavingTexture = () => jointTexture(MAT.paving, MAT.pavingJoint, 1.0, 0.5);

// ── Signs ────────────────────────────────────────────────────────────────────

interface SignCanvas {
  canvas: HTMLCanvasElement;
  texture: THREE.CanvasTexture;
  text: string;
  color: string;
  arabic: boolean;
}

const signs: SignCanvas[] = [];

function paintSign(s: SignCanvas) {
  const g = s.canvas.getContext('2d')!;
  g.clearRect(0, 0, s.canvas.width, s.canvas.height);
  g.fillStyle = s.color;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.direction = s.arabic ? 'rtl' : 'ltr';
  // Latin brands in wide capitals, the way the built fascias carry them.
  g.font = s.arabic ? `600 150px ${FONT_STACK}` : `600 140px ${FONT_STACK}`;
  const text = s.arabic ? s.text : s.text.toUpperCase().split('').join(' ');
  g.fillText(text, s.canvas.width / 2, s.canvas.height / 2, s.canvas.width * 0.94);
  s.texture.needsUpdate = true;
}

// The canvas draws in whatever face is loaded; repaint once the web font is.
document.fonts?.ready.then(() => signs.forEach(paintSign)).catch(() => {});

/**
 * A sign on a fascia: the brand of a leased unit, or «متاح للتأجير» on one
 * that is free. Unlit, so it reads at dusk the way a lit sign would.
 */
export function signMesh(text: string, available: boolean, widthM: number) {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 192;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  const sign: SignCanvas = {
    canvas,
    texture,
    text,
    color: available ? mint[300] : '#f4efe6',
    arabic: available,
  };
  signs.push(sign);
  paintSign(sign);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
  });
  const height = Math.min(0.62, widthM * 0.19);
  return new THREE.Mesh(new THREE.PlaneGeometry(widthM, height), material);
}

// ── Furniture ────────────────────────────────────────────────────────────────

function inside([x, y]: Point, polygon: Point[]) {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** Tables on a grid, kept `clear` metres from the walls. */
export function tableSpots(polygon: Point[], step = 4.2, clear = 1.3): Point[] {
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const spots: Point[] = [];
  for (let x = Math.min(...xs) + clear; x <= Math.max(...xs) - clear; x += step) {
    for (let y = Math.min(...ys) + clear; y <= Math.max(...ys) - clear; y += step) {
      const ok = [
        [x, y],
        [x - clear, y],
        [x + clear, y],
        [x, y - clear],
        [x, y + clear],
      ].every((p) => inside(p as Point, polygon));
      if (ok) spots.push([x, y]);
    }
  }
  return spots;
}

/**
 * One instanced set of tables, chairs and (outdoors) umbrellas for a whole
 * floor, so a few hundred pieces cost four draw calls.
 */
export function furniture(
  spots: Point[],
  y0: number,
  cx: number,
  cz: number,
  materials: { top: THREE.Material; chair: THREE.Material; canopy?: THREE.Material },
) {
  const group = new THREE.Group();
  if (!spots.length) return group;
  const top = new THREE.CylinderGeometry(0.42, 0.42, 0.04, 20);
  const leg = new THREE.CylinderGeometry(0.04, 0.06, 0.72, 6);
  const chair = new THREE.BoxGeometry(0.42, 0.8, 0.42);
  const pole = new THREE.CylinderGeometry(0.03, 0.03, 2.5, 6);
  const canopy = new THREE.ConeGeometry(1.35, 0.45, 8, 1, true);

  const umbrellas = materials.canopy ? spots.filter((_, i) => i % 2 === 0) : [];
  const tops = new THREE.InstancedMesh(top, materials.top, spots.length);
  const legs = new THREE.InstancedMesh(leg, materials.chair, spots.length);
  const chairs = new THREE.InstancedMesh(chair, materials.chair, spots.length * 4);
  const m = new THREE.Matrix4();
  spots.forEach(([x, y], i) => {
    tops.setMatrixAt(i, m.makeTranslation(x - cx, y0 + 0.75, y - cz));
    legs.setMatrixAt(i, m.makeTranslation(x - cx, y0 + 0.36, y - cz));
    [
      [0.75, 0],
      [-0.75, 0],
      [0, 0.75],
      [0, -0.75],
    ].forEach(([dx, dz], k) => {
      chairs.setMatrixAt(i * 4 + k, m.makeTranslation(x - cx + dx!, y0 + 0.42, y - cz + dz!));
    });
  });
  const meshes: THREE.InstancedMesh[] = [tops, legs, chairs];
  if (materials.canopy && umbrellas.length) {
    const poles = new THREE.InstancedMesh(pole, materials.chair, umbrellas.length);
    const canopies = new THREE.InstancedMesh(canopy, materials.canopy, umbrellas.length);
    umbrellas.forEach(([x, y], i) => {
      poles.setMatrixAt(i, m.makeTranslation(x - cx, y0 + 1.25, y - cz));
      canopies.setMatrixAt(i, m.makeTranslation(x - cx, y0 + 2.55, y - cz));
    });
    meshes.push(poles, canopies);
  }
  for (const mesh of meshes) {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}

// ── Planting ─────────────────────────────────────────────────────────────────

const leafColors = MAT.leaves.map((c) => new THREE.Color(c));

/** Shrubs along the planter walls, as the built terraces have. */
export function shrubs(runs: Run[], y0: number, cx: number, cz: number, material: THREE.Material) {
  const spots: [number, number, number][] = [];
  for (const run of runs) {
    for (let t = run.t0 + 0.4; t < run.t1 - 0.3; t += 0.7) {
      const x = run.origin[0] + run.dir[0] * t - run.out[0] * 0.25;
      const y = run.origin[1] + run.dir[1] * t - run.out[1] * 0.25;
      spots.push([x - cx, y - cz, 0.3 + Math.random() * 0.2]);
    }
  }
  const mesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 1), material, spots.length);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  spots.forEach(([x, z, r], i) => {
    q.setFromEuler(new THREE.Euler(0, Math.random() * Math.PI, 0));
    m.compose(new THREE.Vector3(x, y0 + r * 0.6, z), q, new THREE.Vector3(r, r * 0.85, r));
    mesh.setMatrixAt(i, m);
    mesh.setColorAt(i, leafColors[i % leafColors.length]!);
  });
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Broadleaf trees and date palms, instanced: a few dozen trees cost six draw
 * calls. Each tree is a trunk and three to five leaf clumps.
 */
function trees(spots: { x: number; z: number; palm: boolean }[]) {
  const group = new THREE.Group();
  const leafMaterial = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.95 });
  const trunkMaterial = new THREE.MeshStandardMaterial({ color: MAT.trunk, roughness: 0.9 });
  const broad = spots.filter((s) => !s.palm);
  const palms = spots.filter((s) => s.palm);

  const trunk = new THREE.CylinderGeometry(0.18, 0.28, 3.2, 8);
  const clump = new THREE.IcosahedronGeometry(1, 1);
  const trunks = new THREE.InstancedMesh(trunk, trunkMaterial, broad.length);
  const clumps = new THREE.InstancedMesh(clump, leafMaterial, broad.length * 4);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  broad.forEach(({ x, z }, i) => {
    trunks.setMatrixAt(i, m.makeTranslation(x, 1.6, z));
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2 + i;
      const r = 1.3 + Math.random() * 0.8;
      m.compose(
        new THREE.Vector3(x + Math.cos(a) * 0.9, 3.6 + Math.random() * 1.2, z + Math.sin(a) * 0.9),
        q,
        new THREE.Vector3(r, r * 0.85, r),
      );
      clumps.setMatrixAt(i * 4 + k, m);
      clumps.setColorAt(i * 4 + k, leafColors[(i + k) % leafColors.length]!);
    }
  });

  // Date palms: a tall bare trunk and a crown of drooping fronds.
  const palmTrunk = new THREE.CylinderGeometry(0.2, 0.3, 7, 8);
  const frond = new THREE.ConeGeometry(0.35, 3.6, 4);
  frond.translate(0, 1.8, 0);
  frond.rotateX(Math.PI / 2.6);
  const palmTrunks = new THREE.InstancedMesh(palmTrunk, trunkMaterial, palms.length);
  const fronds = new THREE.InstancedMesh(frond, leafMaterial, palms.length * 9);
  palms.forEach(({ x, z }, i) => {
    palmTrunks.setMatrixAt(i, m.makeTranslation(x, 3.5, z));
    for (let k = 0; k < 9; k++) {
      q.setFromEuler(new THREE.Euler(0, (k / 9) * Math.PI * 2 + i, 0));
      m.compose(new THREE.Vector3(x, 7, z), q, new THREE.Vector3(1, 1, 1));
      fronds.setMatrixAt(i * 9 + k, m);
      fronds.setColorAt(i * 9 + k, leafColors[k % 2]!);
    }
  });

  for (const mesh of [trunks, clumps, palmTrunks, fronds]) {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}

// ── Roof plant ───────────────────────────────────────────────────────────────

/** Air-handling units over each core, sized to it: the usual place for them. */
export function roofPlant(cores: Point[][], y0: number, cx: number, cz: number) {
  const parts: THREE.BufferGeometry[] = [];
  for (const core of cores) {
    const xs = core.map((p) => p[0]);
    const ys = core.map((p) => p[1]);
    const w = Math.max(...xs) - Math.min(...xs);
    const d = Math.max(...ys) - Math.min(...ys);
    if (w < 2 || d < 2) continue;
    const bw = Math.min(w * 0.6, 4.5);
    const bd = Math.min(d * 0.6, 3);
    const box = new THREE.BoxGeometry(bw, 1.6, bd);
    box.translate(
      (Math.min(...xs) + Math.max(...xs)) / 2 - cx,
      y0 + 0.8,
      (Math.min(...ys) + Math.max(...ys)) / 2 - cz,
    );
    parts.push(box);
  }
  return parts;
}

// ── The tower crane ──────────────────────────────────────────────────────────

/**
 * A tower crane for «مراحل البناء»: a lattice mast, a slewing jib with its
 * counter-jib and weights, a cab and a hook line. On site through the frame
 * and the envelope, gone for fit-out. The stage turns `slew` and fades `materials`.
 */
export function crane() {
  const group = new THREE.Group();
  const slew = new THREE.Group();
  const white = new THREE.MeshStandardMaterial({
    color: '#ece7dc',
    roughness: 0.55,
    metalness: 0.3,
    transparent: true,
  });
  const dark = new THREE.MeshStandardMaterial({
    color: MAT.charcoal,
    roughness: 0.6,
    metalness: 0.4,
    transparent: true,
  });
  const mastHeight = 38;
  // The mast as a lattice: four legs and a brace every 2.5 m.
  const legs: THREE.BufferGeometry[] = [];
  for (const [x, z] of [
    [-0.9, -0.9],
    [0.9, -0.9],
    [0.9, 0.9],
    [-0.9, 0.9],
  ] as const) {
    const leg = new THREE.BoxGeometry(0.18, mastHeight, 0.18);
    leg.translate(x, mastHeight / 2, z);
    legs.push(leg);
  }
  for (let y = 1.25; y < mastHeight; y += 2.5) {
    for (const r of [0, Math.PI / 2]) {
      const brace = new THREE.BoxGeometry(2.55, 0.08, 0.08);
      brace.rotateZ(Math.PI / 4);
      brace.rotateY(r);
      brace.translate(r === 0 ? 0 : 0.9, y, r === 0 ? 0.9 : 0);
      legs.push(brace);
      const back = brace.clone();
      back.translate(r === 0 ? 0 : -1.8, 0, r === 0 ? -1.8 : 0);
      legs.push(back);
    }
  }
  group.add(new THREE.Mesh(mergeGeometries(legs, false), white));

  const jib = new THREE.Mesh(new THREE.BoxGeometry(34, 1.2, 1.2), white);
  jib.position.set(15, mastHeight + 1, 0);
  const counter = new THREE.Mesh(new THREE.BoxGeometry(11, 1.0, 1.4), white);
  counter.position.set(-6.5, mastHeight + 1, 0);
  const weights = new THREE.Mesh(new THREE.BoxGeometry(3, 2.4, 2.2), dark);
  weights.position.set(-10, mastHeight - 0.2, 0);
  const cab = new THREE.Mesh(new THREE.BoxGeometry(2.2, 2.2, 2), dark);
  cab.position.set(1.6, mastHeight - 0.4, 1.4);
  const apex = new THREE.Mesh(new THREE.ConeGeometry(0.9, 6, 4), white);
  apex.position.set(0, mastHeight + 4.4, 0);
  const trolley = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.6, 1.6), dark);
  trolley.position.set(20, mastHeight + 0.1, 0);
  const line = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 22, 4), dark);
  line.position.set(20, mastHeight - 11, 0);
  const hook = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.8, 0.8), dark);
  hook.position.set(20, mastHeight - 22, 0);
  slew.add(jib, counter, weights, cab, apex, trolley, line, hook);
  group.add(slew);
  group.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) o.castShadow = true;
  });
  return { group, slew, materials: [white, dark] };
}

/** A façade of windows, about a third of them lit, warm or cool, for a tower at night. */
function windowTexture(columns: number, rows: number) {
  const canvas = document.createElement('canvas');
  canvas.width = columns * 8;
  canvas.height = rows * 8;
  const g = canvas.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, canvas.width, canvas.height);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < columns; c++) {
      if (Math.random() > 0.34) continue;
      g.fillStyle = Math.random() < 0.75 ? '#ffcf8f' : '#cfe1ff';
      g.globalAlpha = 0.45 + Math.random() * 0.55;
      g.fillRect(c * 8 + 1, r * 8 + 2, 6, 4);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  return texture;
}

// ── Surroundings ─────────────────────────────────────────────────────────────

/**
 * Lawn, a paved plaza, the sea beyond a promenade on the courtyard side (as
 * photo matal-4 sees it through the entrance), trees and palms, and three
 * towers behind for scale. Positions are guesses.
 */
export function surroundings(halfW: number, halfD: number) {
  const group = new THREE.Group();
  const shore = -(halfD + 25);

  // Land stops at the shore, so the sea below it shows; the sea wall is the step.
  const lawn = new THREE.Mesh(
    new THREE.PlaneGeometry(1400, 700),
    new THREE.MeshStandardMaterial({ color: MAT.lawn, roughness: 1 }),
  );
  lawn.rotation.x = -Math.PI / 2;
  lawn.position.set(0, -0.03, shore + 350);
  lawn.receiveShadow = true;
  group.add(lawn);

  const paving = pavingTexture();
  const plazaW = halfW * 2 + 50;
  const plazaD = halfD * 2 + 50;
  paving.repeat.set(plazaW, plazaD * 2);
  const plaza = new THREE.Mesh(
    new THREE.PlaneGeometry(plazaW, plazaD),
    new THREE.MeshStandardMaterial({ map: paving, roughness: 0.9 }),
  );
  plaza.rotation.x = -Math.PI / 2;
  plaza.position.set(0, -0.01, shore + plazaD / 2);
  plaza.receiveShadow = true;
  group.add(plaza);

  // The sea: rippling normals scrolled by the stage, reflecting the sky.
  const ripples = waterNormals();
  ripples.repeat.set(140, 60);
  const water = new THREE.MeshStandardMaterial({
    color: MAT.water,
    roughness: 0.06,
    metalness: 0.15,
    normalMap: ripples,
    normalScale: new THREE.Vector2(0.28, 0.28),
  });
  const sea = new THREE.Mesh(new THREE.PlaneGeometry(1400, 600), water);
  sea.rotation.x = -Math.PI / 2;
  sea.position.set(0, -0.6, shore - 300.5);
  sea.receiveShadow = true;
  group.add(sea);

  // The sea wall, and the step down to the water.
  const wall = new THREE.Mesh(
    new THREE.BoxGeometry(1400, 1.2, 1.2),
    new THREE.MeshStandardMaterial({ color: MAT.stone, roughness: 0.9 }),
  );
  wall.position.set(0, -0.5, shore - 0.4);
  wall.receiveShadow = true;
  group.add(wall);

  // Trees round the plaza, palms along the promenade.
  const spots: { x: number; z: number; palm: boolean }[] = [];
  for (let x = -halfW - 20; x <= halfW + 20; x += 12) {
    spots.push({ x: x + 6, z: shore + 6, palm: true });
  }
  for (let z = shore + 20; z <= halfD + 20; z += 11) {
    spots.push({ x: -halfW - 14, z, palm: false }, { x: halfW + 14, z: z + 5, palm: false });
  }
  for (let x = -halfW; x <= halfW; x += 13) spots.push({ x, z: halfD + 18, palm: false });
  group.add(trees(spots));

  // Towers behind, in blue-grey glass with white floor bands, and at night
  // a scatter of lit windows — no tower is ever wholly dark.
  const towerWindows: THREE.MeshStandardMaterial[] = [];
  const band = new THREE.MeshStandardMaterial({ color: MAT.towerBand, roughness: 0.7 });
  const towers: [number, number, number, number, number][] = [
    // x, z, width, depth, height
    [halfW + 80, halfD + 10, 26, 22, 84],
    [halfW + 25, halfD + 85, 34, 20, 58],
    [-halfW - 20, halfD + 95, 22, 22, 110],
  ];
  for (const [x, z, w, d, h] of towers) {
    const glass = new THREE.MeshStandardMaterial({
      color: MAT.towerGlass,
      roughness: 0.12,
      metalness: 0.75,
      emissive: new THREE.Color('#ffffff'),
      emissiveMap: windowTexture(Math.round(w / 3), Math.floor(h / 3.5)),
      emissiveIntensity: 0,
    });
    towerWindows.push(glass);
    const tower = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), glass);
    tower.position.set(x, h / 2, z);
    tower.castShadow = true;
    tower.receiveShadow = true;
    group.add(tower);
    const bands = new THREE.InstancedMesh(
      new THREE.BoxGeometry(w + 0.4, 0.35, d + 0.4),
      band,
      Math.floor(h / 3.5),
    );
    const m = new THREE.Matrix4();
    for (let i = 0; i < bands.count; i++)
      bands.setMatrixAt(i, m.makeTranslation(x, 3.5 * (i + 1), z));
    group.add(bands);
  }

  // Lamp posts along the promenade. Their heads are what the stage lights at dusk.
  const lampHead = new THREE.MeshStandardMaterial({
    color: '#fff6e8',
    emissive: new THREE.Color(MAT.warmLight),
    emissiveIntensity: 0,
    roughness: 0.4,
  });
  const postMaterial = new THREE.MeshStandardMaterial({ color: MAT.charcoal, roughness: 0.5 });
  const post = new THREE.CylinderGeometry(0.07, 0.09, 4.2, 8);
  const head = new THREE.SphereGeometry(0.28, 12, 8);
  const xs: number[] = [];
  for (let x = -halfW - 40; x <= halfW + 40; x += 12) xs.push(x);
  const posts = new THREE.InstancedMesh(post, postMaterial, xs.length);
  const heads = new THREE.InstancedMesh(head, lampHead, xs.length);
  const m = new THREE.Matrix4();
  xs.forEach((x, i) => {
    posts.setMatrixAt(i, m.makeTranslation(x, 2.1, shore + 3));
    heads.setMatrixAt(i, m.makeTranslation(x, 4.35, shore + 3));
  });
  posts.castShadow = true;
  group.add(posts, heads);

  // The pool of light each lamp throws on the paving, and more under the
  // trees round the plaza: soft warm discs, added on top, seen only at dusk.
  const glowCanvas = document.createElement('canvas');
  glowCanvas.width = glowCanvas.height = 128;
  const g = glowCanvas.getContext('2d')!;
  const radial = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  radial.addColorStop(0, 'rgba(255,214,160,0.9)');
  radial.addColorStop(0.35, 'rgba(255,190,120,0.35)');
  radial.addColorStop(1, 'rgba(255,170,100,0)');
  g.fillStyle = radial;
  g.fillRect(0, 0, 128, 128);
  const glowTexture = new THREE.CanvasTexture(glowCanvas);
  glowTexture.colorSpace = THREE.SRGBColorSpace;
  const pools = new THREE.MeshBasicMaterial({
    map: glowTexture,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const poolSpots: [number, number, number][] = [
    ...xs.map((x): [number, number, number] => [x, shore + 3, 11]),
    ...spots.filter((t) => !t.palm).map((t): [number, number, number] => [t.x, t.z, 8]),
  ];
  const disc = new THREE.PlaneGeometry(1, 1);
  disc.rotateX(-Math.PI / 2);
  const discs = new THREE.InstancedMesh(disc, pools, poolSpots.length);
  const sm = new THREE.Matrix4();
  poolSpots.forEach(([x, z, size], i) =>
    discs.setMatrixAt(
      i,
      sm.compose(
        new THREE.Vector3(x, 0.04, z),
        new THREE.Quaternion(),
        new THREE.Vector3(size, 1, size),
      ),
    ),
  );
  discs.renderOrder = 1;
  group.add(discs);

  return { group, lampHead, ripples, pools, towerWindows };
}
